<script setup lang="ts">
import { useActiveElement } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Select from '../ui/select/Select.vue';
import SelectContent from '../ui/select/SelectContent.vue';
import SelectGroup from '../ui/select/SelectGroup.vue';
import SelectItem from '../ui/select/SelectItem.vue';
import SelectTrigger from '../ui/select/SelectTrigger.vue';
import SelectValue from '../ui/select/SelectValue.vue';
import messages from './InputPhone.locale.json';

import { cn } from '@/utils';
import { Mask } from 'maska';
import type { CountryPhoneData } from '~/core/ports/entities/phone';
import {
	countriesPhoneDataByDDI,
	countriesPhoneDataList
} from '~/utils/phone/countries';
import type { InputPhoneProps } from '.';
import type { Input } from '../ui/input';

const props = withDefaults(defineProps<InputPhoneProps>(), {
	disabled: false,
	required: false,
	autofocus: false
});

defineEmits<{
	(e: 'blur'): void;
}>();

const { t } = useI18n({
	useScope: 'local',
	messages
});

const modelValue = defineModel<InputPhoneProps['modelValue']>({
	required: true
});

const iso = shallowRef('BR');
const number = shallowRef('');
const maskedNumber = ref<string>('');
const phoneInput = useTemplateRef<typeof Input>('phoneInput');

const country = computed<CountryPhoneData | null>(
	() => countriesPhoneDataByDDI[iso.value] || null
);
const mask = computed(() => country.value?.mask);

watch([iso, number], ([_newIso, newNumber]) => {
	if (country.value) {
		const newModelValue = country.value.code + newNumber;

		if (modelValue.value === newModelValue) return;

		modelValue.value = newModelValue;
	}
});

watch(
	modelValue,
	newValue => {
		const countryCode = country.value?.code;

		if (!countryCode) return;

		const newPhoneNumber = newValue?.replace(countryCode, '') || '';

		if (number.value === newPhoneNumber) return;

		const maskObject = new Mask({ mask: mask.value });

		number.value = newPhoneNumber;
		maskedNumber.value = maskObject.masked(newPhoneNumber);
	},
	{ immediate: true }
);

const veeValidateAttrs = computed<
	Pick<InputPhoneProps, 'onBlur' | 'onChange' | 'onInput' | 'required'>
>(() => ({
	onBlur: props.onBlur,
	onChange: props.onChange,
	onInput: props.onInput,
	required: props.required
}));

defineExpose({ number });
</script>

<template>
	<div
		:class="[
			cn('flex gap-[1px] border border-white-dark-4', wrapperClass),
			{ '!border-error': error },
			{ '!border-primary': phoneInput?.focused }
		]"
	>
		<Select
			v-model="iso"
			data-testid="select-phone"
			:class="cn('h-[50px] items-center border-none px-3')"
			:disabled="disabled"
		>
			<SelectTrigger
				:class="
					cn(
						'h-[50px] w-min min-w-[95px] items-center border-none bg-secondary-dark-1 py-2 pl-3 pr-2 shadow-none',
						selectTriggerClass
					)
				"
				:arrow-class="cn('text-primary', selectArrowClass)"
			>
				<div :class="cn('flex flex-col', selectPlaceholderClasses?.wrapper)">
					<SelectValue>
						<div
							:class="cn('flex items-center gap-1', selectItemClasses?.wrapper)"
						>
							<img
								:src="country?.flag"
								:alt="`${country?.name} Flag`"
								:class="cn('h-3 w-[18px]', selectItemClasses?.flag)"
							/>
							<span
								:class="
									cn(
										'text-sm font-normal leading-[150%] text-primary',
										selectItemClasses?.label
									)
								"
							>
								{{ country?.code }}
							</span>
						</div>
					</SelectValue>
				</div>
			</SelectTrigger>

			<SelectContent class="bg-white">
				<SelectGroup>
					<SelectItem
						v-for="item in countriesPhoneDataList"
						:key="`country-phone-option:${item.name}`"
						:value="item.ddi"
						class="data-[highlighted]:bg-white-dark-3"
					>
						<div
							:class="cn('flex items-center gap-1', selectItemClasses?.wrapper)"
						>
							<img
								:src="item.flag"
								:alt="`${item.name} Flag`"
								:class="cn('h-3 w-[18px]', selectItemClasses?.flag)"
							/>
							<p
								:class="
									cn(
										'text-sm font-normal leading-[150%] text-primary',
										selectItemClasses?.label
									)
								"
							>
								{{ item.code }}
							</p>
						</div>
					</SelectItem>
				</SelectGroup>
			</SelectContent>
		</Select>

		<Input
			ref="phoneInput"
			v-model="maskedNumber"
			v-maska:number.unmasked="mask"
			:autofocus="autofocus"
			data-testid="input-phone"
			type="text"
			:placeholder="t('input.placeholder')"
			:class="
				cn(
					'focus-visible:ring-none h-[50px] w-full border-none px-4 focus-visible:ring-0 disabled:bg-secondary-dark-1',
					inputClass
				)
			"
			:disabled="disabled"
			v-bind="veeValidateAttrs"
			@blur="$emit('blur')"
		/>
	</div>
</template>
